import type { RawExtract, RawWork } from './ExtractRawView2'
import type { WorkTypeCode } from '../context/CommonCodesContext'

// 견적서 항목 한 줄 — 원문 순서대로, 소속이 있으면 부모 아래 들여써서 나열하기 위한 행
export type RawRow = {
  key:     string         // 표준 's{n}' / 미매칭 'n{n}' / 묶음 제목 'h{n}' (미매칭 선택값의 키로도 쓴다)
  rawText: string
  work:    RawWork
  matched: boolean
  /** 번호 — 최상위 '3', 하위 '3-1' */
  no:      string
  /** 0=최상위, 1=상세에서 찾은 하위 항목 */
  depth:   0 | 1
  /** 묶음 이름만 보여 주는 제목 행(공종이 아님 — 연결·선택 없음) */
  header?: boolean
}

type Item = { key: string; rawText: string; work: RawWork; matched: boolean }

const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase()

/**
 * 표준·미매칭 항목을 견적서 원문 순서(seq)로 합치고, group(소속 묶음)이 있는 항목은 같은 이름의 최상위 행 아래에 하위(3-1)로 붙인다.
 * 같은 이름의 최상위 행이 없으면(예: 기타공사) 묶음 제목 행을 만들어 그 아래에 둔다. seq 가 하나라도 없으면 표준 → 미매칭 순.
 */
export function buildRows(raw: RawExtract): RawRow[] {
  const std = (raw.standardWorks ?? []).map((w, i): Item => ({ key: `s${i}`, rawText: w.rawText, work: w, matched: !!w.matchedCode }))
  const non = (raw.nonStandardWorks ?? []).map((w, i): Item => ({ key: `n${i}`, rawText: w.rawText, work: w, matched: false }))
  const all = [...std, ...non]
  const ordered = all.every(r => r.work.seq != null) ? [...all].sort((a, b) => a.work.seq! - b.work.seq!) : all

  const tops = ordered.filter(r => !r.work.group?.trim())
  const parentOf = (r: Item) => tops.find(t => norm(t.rawText) === norm(r.work.group ?? ''))

  const out: RawRow[] = []
  const headerDone = new Set<string>()
  let top = 0
  const pushChildren = (children: Item[], parentCode: string | null, topNo: number) => {
    let sub = 0
    children.forEach(c => {
      if (c.matched && parentCode && c.work.matchedCode === parentCode) return // 부모와 같은 공종이면 중복이라 뺀다
      sub += 1
      out.push({ ...c, no: `${topNo}-${sub}`, depth: 1 })
    })
  }

  ordered.forEach(r => {
    const g = r.work.group?.trim()
    if (!g) {
      top += 1
      out.push({ ...r, no: String(top), depth: 0 })
      pushChildren(ordered.filter(c => c.work.group?.trim() && parentOf(c) === r), r.work.matchedCode, top)
      return
    }
    if (parentOf(r)) return // 부모 행 아래에서 이미 나왔다
    const gk = norm(g)
    if (headerDone.has(gk)) return
    headerDone.add(gk)
    top += 1
    out.push({
      key: `h${top}`, rawText: g, matched: true, no: String(top), depth: 0, header: true,
      work: { rawText: g, matchedCode: null, matchedName: null, standard: false, duration: null, color: null },
    })
    pushChildren(ordered.filter(c => norm(c.work.group ?? '') === gk && !parentOf(c)), null, top)
  })
  return out
}

const normalize = (s: string) => s.replace(/[\s\-_/·.,()[\]{}]/g, '').toLowerCase()

/**
 * 미매칭 원문에 어울리는 공종 후보(최대 limit 개) — 공종 이름과의 포함 관계·겹치는 글자로 점수를 낸다.
 * (서버가 후보를 내려주지 않는 응답에서만 쓰는 임시 계산)
 */
export function suggestWorkTypes(rawText: string, workTypes: WorkTypeCode[], excludeCodes: Set<string>, limit = 2): WorkTypeCode[] {
  const text = normalize(rawText)
  if (!text) return []
  return workTypes
    .filter(w => w.scheduleOnlyYn !== 'Y' && !excludeCodes.has(w.workTypeCode))
    .map(w => {
      const name = normalize(w.workTypeName)
      if (!name) return { w, score: 0 }
      if (text.includes(name) || name.includes(text)) return { w, score: 100 + name.length }
      const uniq = [...new Set(name)]
      const common = uniq.filter(c => text.includes(c)).length
      const ratio = common / uniq.length
      return { w, score: common >= 2 && ratio >= 0.5 ? ratio * 50 : 0 }
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(x => x.w)
}

/** 테스트 진입용 샘플 — 실제 공종 마스터 이름으로 만들어 후보 추천·하이라이트·계층 표시를 확인할 수 있게 한다. */
export function buildSampleRaw(workTypes: WorkTypeCode[]): RawExtract {
  const pool = workTypes.filter(w => w.scheduleOnlyYn !== 'Y')
  const matched = pool.slice(0, 5)
  const near = pool.slice(5, 7)
  const kids = pool.slice(7, 9)
  const std: RawWork[] = matched.map((w, i) => ({
    rawText: `${w.workTypeName} 공사 일체`, matchedCode: w.workTypeCode, matchedName: w.workTypeName,
    standard: true, duration: 2 + i, color: w.workTypeColor, seq: [1, 2, 4, 6, 8][i],
    detail: `${w.workTypeName} 세부 1, ${w.workTypeName} 세부 2`,
  }))
  const non: RawWork[] = [
    { rawText: near[0] ? `${near[0].workTypeName} 부분 보수(자재 포함)` : '부분 보수(자재 포함)', seq: 3, detail: '부분 철거, 자재 교체' },
    { rawText: '특수 마감 시공(아트월)', seq: 5, detail: null },
    { rawText: near[1] ? `${near[1].workTypeName} 기타` : '기타 마감', seq: 7, detail: null },
  ].map(x => ({ ...x, matchedCode: null, matchedName: null, standard: false, duration: null, color: null }))

  // 상세에서 찾은 항목(계층 표시 확인용) — 첫 항목 아래 1건, 기타공사 묶음 아래 2건(매칭 1·미매칭 1)
  if (kids[0] && std[0]) {
    std.push({
      rawText: `${kids[0].workTypeName} 처리`, matchedCode: kids[0].workTypeCode, matchedName: kids[0].workTypeName,
      standard: true, duration: 1, color: kids[0].workTypeColor, seq: 11, group: std[0].rawText,
      detail: `${kids[0].workTypeName} 세부 작업`,
    })
  }
  if (kids[1]) {
    std.push({
      rawText: `${kids[1].workTypeName} 마감`, matchedCode: kids[1].workTypeCode, matchedName: kids[1].workTypeName,
      standard: true, duration: 1, color: kids[1].workTypeColor, seq: 9, group: '기타공사',
      detail: `${kids[1].workTypeName} 마감 세부`,
    })
  }
  non.push({
    rawText: '입주 전 마감 점검', seq: 10, matchedCode: null, matchedName: null, standard: false, duration: null, color: null, group: '기타공사', detail: '점검, 보수',
  })
  return {
    standardWorks: std, nonStandardWorks: non, source: 'sample',
    rawAnswer: '(테스트 샘플 — 실제 분석 결과가 아닙니다)', analysisStatus: 'SUCCESS', sheetName: null,
  }
}
